import { BlitzPage } from "@blitzjs/next"
import { useMutation } from "@blitzjs/rpc"
import Layout from "app/core/layouts/Layout"
import Footer from "components/footer"
import dynamic from "next/dynamic"
// Dynamic import to avoid SSR and hydration errors
const Header = dynamic(() => import("components/header"), { ssr: false })
import { Formik, Form, Field, ErrorMessage } from "formik"
import { z } from "zod"
import { Suspense, useState } from "react"
import { STATIC_ASSETS_CLOUDFRONT_URL } from "app/constants"
import { validateZodSchema } from "blitz"
import sendContactMessage from "app/contact/mutations/sendContactMessage"

const Contact: BlitzPage = () => {
  const schema = z.object({
    name: z.string().max(30).optional(),
    email: z.string().email("Invalid email"),
    organization: z.string().optional(),
    comment: z.string(),
    questionType: z.string(),
    formVersion: z.literal("v2"),
    website: z.string().optional(),
  })

  const QUESTION_TYPES = [
    "Technical / Data",
    "Report a bug or issue",
    "City partnerships / goals",
    "Frontline community org partnerships / goals",
    "Technical tools / implementation",
    "Media inquiries and social media toolkit",
    "Donations",
    "General feedback",
  ]

  // Define initial form values
  const initialValues = {
    name: "",
    email: "",
    organization: "",
    comment: "",
    questionType: QUESTION_TYPES[0], // New field for question type
    formVersion: "v2" as const, // Anti-spam: must be present and match
    website: "", // Honeypot; must stay empty
  }

  const ContactForm = () => {
    const [sent, setSent] = useState<"init" | "sending" | "sent">("init")
    const [sendContactMessageMutation] = useMutation(sendContactMessage)

    const handleSubmit = async (values: {
      name: string
      email: string
      organization: string
      comment: string
      questionType: string
      formVersion: "v2"
      website: string
    }) => {
      setSent("sending")
      await sendContactMessageMutation(values)
      setSent("sent")
    }

    return (
      <div className="text-gray-800">
        <h1 className="mb-2 py-6 pl-8 text-3xl font-semibold lg:text-[40px]">Get in touch</h1>
        {sent === "sent" && <div>Thanks for reaching out! Your message has been sent.</div>}
        {sent === "sending" && <div>Your message is sending...</div>}
        {sent === "init" && (
          <div className="px-8">
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validate={async (val) => {
                return await validateZodSchema(schema)(val)
              }}
              validateOnChange={false}
            >
              <Form>
                <div
                  style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}
                  aria-hidden="true"
                >
                  <label htmlFor="website">Website</label>
                  <Field type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="mb-4">
                  <label htmlFor="questionType" className="mb-1 block font-semibold">
                    What type of question or comment do you have?
                  </label>
                  <Field
                    as="select"
                    id="questionType"
                    name="questionType"
                    className="w-full rounded border border-gray-500 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  >
                    {QUESTION_TYPES.map((type, i) => (
                      <option key={i} value={type}>
                        {type}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="questionType" component="div" className="text-red-500" />
                </div>

                <div className="mb-4">
                  <label htmlFor="name" className="mb-1 block font-semibold">
                    Name
                  </label>
                  <Field
                    type="text"
                    id="name"
                    name="name"
                    className="w-full rounded border border-gray-500 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  />
                  <ErrorMessage name="name" component="div" className="text-red-500" />
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="mb-1 block font-semibold">
                    Email
                  </label>
                  <Field
                    type="email"
                    id="email"
                    name="email"
                    className="w-full rounded border border-gray-500 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  />
                  <ErrorMessage name="email" component="div" className="text-red-500" />
                </div>

                <div className="mb-4">
                  <label htmlFor="organization" className="mb-1 block font-semibold">
                    Organization
                  </label>
                  <Field
                    type="text"
                    id="organization"
                    name="organization"
                    rows={4}
                    className="w-full rounded border border-gray-500 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  />
                  <ErrorMessage name="message" component="div" className="text-red-500" />
                </div>

                <div className="mb-4">
                  <label htmlFor="comment" className="mb-1 block font-semibold">
                    Comments
                  </label>
                  <Field
                    as="textarea"
                    id="comment"
                    name="comment"
                    rows={4}
                    className="w-full rounded border border-gray-500 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  />
                  <ErrorMessage name="comment" component="div" className="text-red-500" />
                </div>
                <div className="flex flex-row items-center justify-center">
                  <button
                    type="submit"
                    className="rounded-full bg-brand-green-dark px-6 py-2 font-semibold uppercase tracking-wider text-white hover:bg-brand-green-darker"
                  >
                    Send
                  </button>
                </div>
                <div className="h-6"></div>
              </Form>
            </Formik>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#ECF5F2]">
      <div
        style={{
          backgroundImage: `url(${STATIC_ASSETS_CLOUDFRONT_URL}/Parcel_Tree_Banner_DarkerGr_gdt.jpg)`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          imageRendering: "crisp-edges",
        }}
      >
        <div className="bg-white">
          <Header />
        </div>
        <div className="h-80"></div>
      </div>

      <div className="m-auto -mt-56 w-11/12 md:w-4/5 xl:w-1/2">
        <div className="rounded-lg bg-white p-4 shadow-lg">
          <Suspense fallback={null}>
            <ContactForm />
          </Suspense>
        </div>
      </div>

      <div className="h-20"></div>
      <Footer />
    </div>
  )
}

Contact.getLayout = (page) => <Layout title={"Contact Us"}>{page}</Layout>
export default Contact
